import React, { useState, useEffect, useCallback } from 'react';
import { RefreshCw, Plus, BarChart3, TrendingUp, DollarSign, Target, X, Calendar, ChevronRight, ChevronDown } from 'lucide-react';
import { useApp } from '../contexts/AppContext';
import { useAuth } from '../contexts/AuthContext';
import { db } from '../lib/firebase';
import { collection, query, where, onSnapshot } from 'firebase/firestore';
import { formatNumber, formatCurrency, formatPercent, DATE_RANGE_PRESETS, DATE_RANGE_LABELS, getDateRange, getDateRangeDescription } from '../utils/helpers';
import PlatformConnectModal from '../components/PlatformConnectModal';
import ConfirmationModal from '../components/ConfirmationModal';
import CampaignDetailRow from '../components/CampaignDetailRow';

const CampaignMonitoring = () => {
    const { selectedBrand } = useApp();
    const { currentUser } = useAuth();
    const [campaigns, setCampaigns] = useState([]);
    const [platformConnections, setPlatformConnections] = useState([]);
    const [loading, setLoading] = useState(true);
    const [fetching, setFetching] = useState(false);
    const [disconnecting, setDisconnecting] = useState(null);
    const [showConnectModal, setShowConnectModal] = useState(false);
    const [showDisconnectModal, setShowDisconnectModal] = useState(false);
    const [connectionToDisconnect, setConnectionToDisconnect] = useState(null);
    const [selectedPlatform, setSelectedPlatform] = useState('all');
    const [selectedDateRange, setSelectedDateRange] = useState(DATE_RANGE_PRESETS.LAST_30_DAYS);
    const [dateRange, setDateRange] = useState(() => getDateRange(DATE_RANGE_PRESETS.LAST_30_DAYS));
    const [expandedCampaignId, setExpandedCampaignId] = useState(null);

    // Real-time Firestore subscription to campaigns
    useEffect(() => {
        if (!selectedBrand || !currentUser) {
            setLoading(false);
            return;
        }

        // Simplified query without orderBy to avoid index requirements
        const q = query(
            collection(db, 'campaigns'),
            where('brandId', '==', selectedBrand.id),
            where('userId', '==', currentUser.uid)
        );

        const unsubscribe = onSnapshot(q, (snapshot) => {
            const data = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            // Sort client-side by spend descending
            data.sort((a, b) => (b.currentMetrics?.spend || 0) - (a.currentMetrics?.spend || 0));
            console.log('Campaigns from Firestore:', data.length);
            setCampaigns(data);
            setLoading(false);
        }, (error) => {
            console.error('Firestore query error:', error);
            setLoading(false);
        });

        return () => unsubscribe();
    }, [selectedBrand, currentUser]);

    // Fetch platform connections
    const fetchConnections = useCallback(async () => {
        if (!selectedBrand || !currentUser) return;
        try {
            const response = await fetch(
                `http://localhost:3001/api/campaigns/connections?userId=${currentUser.uid}&brandId=${selectedBrand.id}`
            );
            const data = await response.json();
            setPlatformConnections(data.connections || []);
        } catch (error) {
            console.error('Error fetching connections:', error);
        }
    }, [selectedBrand, currentUser]);

    useEffect(() => {
        fetchConnections();
    }, [fetchConnections]);

    // Handle date range change - auto-refresh when changed
    const handleDateRangeChange = async (preset) => {
        setSelectedDateRange(preset);
        const newRange = getDateRange(preset);
        setDateRange(newRange);

        // Auto-refresh data with new date range (fetch even if connections list failed to load)
        if (selectedBrand && currentUser) {
            setFetching(true);
            console.log(`Fetching campaigns for date range: ${newRange.startDate} to ${newRange.endDate}`);
            try {
                const response = await fetch('http://localhost:3001/api/campaigns/fetch-data', {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify({
                        userId: currentUser.uid,
                        brandId: selectedBrand.id,
                        forceRefresh: true,
                        dateRange: {
                            startDate: newRange.startDate,
                            endDate: newRange.endDate
                        }
                    })
                });

                if (!response.ok) {
                    const error = await response.json();
                    throw new Error(error.error || 'Failed to fetch campaign data');
                }

                const data = await response.json();
                console.log(`Fetched ${data.campaigns.length} campaigns for ${newRange.label}`, data);

                // Update campaigns state directly from API response for immediate update
                // The Firestore subscription will also pick up changes but this ensures immediate feedback
                if (data.campaigns && data.campaigns.length > 0) {
                    // Add IDs if they don't exist (API response might not have Firestore doc IDs)
                    const campaignsWithIds = data.campaigns.map((c, i) => ({
                        id: c.id || `${c.platform}-${c.platformCampaignId}`,
                        ...c
                    }));
                    campaignsWithIds.sort((a, b) => (b.currentMetrics?.spend || 0) - (a.currentMetrics?.spend || 0));
                    setCampaigns(campaignsWithIds);
                }
            } catch (error) {
                console.error('Error refreshing campaigns:', error);
                // Don't alert on network errors to avoid spamming
                if (!error.message.includes('Failed to fetch')) {
                    alert(`Failed to refresh: ${error.message}`);
                }
            } finally {
                setFetching(false);
            }
        } else {
            console.log('Skipping fetch - missing requirements:', {
                hasBrand: !!selectedBrand,
                hasUser: !!currentUser
            });
        }
    };

    // Show disconnect confirmation modal
    const handleDisconnectClick = (connection) => {
        setConnectionToDisconnect(connection);
        setShowDisconnectModal(true);
    };

    // Disconnect a platform
    const handleDisconnect = async () => {
        if (!connectionToDisconnect) return;

        setDisconnecting(connectionToDisconnect.id);
        setShowDisconnectModal(false);

        try {
            const response = await fetch('http://localhost:3001/api/campaigns/disconnect-platform', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    connectionId: connectionToDisconnect.id,
                    userId: currentUser.uid
                })
            });

            if (!response.ok) {
                const error = await response.json();
                throw new Error(error.error || 'Failed to disconnect');
            }

            // Refresh connections list
            await fetchConnections();
        } catch (error) {
            console.error('Error disconnecting platform:', error);
            alert(error.message);
        } finally {
            setDisconnecting(null);
            setConnectionToDisconnect(null);
        }
    };

    // Fetch fresh campaign data
    const handleRefresh = async () => {
        if (!selectedBrand || !currentUser) return;

        setFetching(true);
        try {
            const response = await fetch('http://localhost:3001/api/campaigns/fetch-data', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    userId: currentUser.uid,
                    brandId: selectedBrand.id,
                    forceRefresh: true,
                    dateRange: {
                        startDate: dateRange.startDate,
                        endDate: dateRange.endDate
                    }
                })
            });

            if (!response.ok) {
                const error = await response.json();
                throw new Error(error.error || 'Failed to fetch campaign data');
            }

            const data = await response.json();
            console.log(`Fetched ${data.campaigns.length} campaigns`);
        } catch (error) {
            console.error('Error refreshing campaigns:', error);
            alert(error.message);
        } finally {
            setFetching(false);
        }
    };

    // Filter campaigns by platform
    const filteredCampaigns = selectedPlatform === 'all'
        ? campaigns
        : campaigns.filter(c => c.platform === selectedPlatform);

    // Calculate stats
    const stats = {
        totalSpend: filteredCampaigns.reduce((sum, c) => sum + (c.currentMetrics?.spend || 0), 0),
        totalRevenue: filteredCampaigns.reduce((sum, c) => sum + (c.currentMetrics?.revenue || 0), 0),
        totalCampaigns: filteredCampaigns.length,
        avgRoas: 0
    };

    if (stats.totalSpend > 0) {
        stats.avgRoas = stats.totalRevenue / stats.totalSpend;
    }

    const activeCampaigns = filteredCampaigns.filter(c => c.status === 'ACTIVE').length;

    // No brand selected
    if (!selectedBrand) {
        return (
            <div className="animate-fadeIn">
                <div style={{
                    textAlign: 'center',
                    padding: 'var(--space-16)',
                    color: 'var(--color-text-secondary)'
                }}>
                    <BarChart3 size={64} style={{ margin: '0 auto var(--space-4)' }} />
                    <h2>Select a Brand</h2>
                    <p>Please select a brand from the dropdown to view campaign monitoring.</p>
                </div>
            </div>
        );
    }

    return (
        <div className="animate-fadeIn">
            {/* Header */}
            <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: 'var(--space-8)'
            }}>
                <div>
                    <h1 style={{ fontSize: 'var(--font-size-4xl)', marginBottom: 'var(--space-2)' }}>
                        Campaign Monitoring
                    </h1>
                    <p className="text-secondary">
                        Track performance across all advertising platforms
                    </p>
                </div>

                <div style={{ display: 'flex', gap: 'var(--space-3)' }}>
                    <button
                        className="btn btn-secondary"
                        onClick={handleRefresh}
                        disabled={fetching || platformConnections.length === 0}
                    >
                        <RefreshCw size={18} className={fetching ? 'spinning' : ''} />
                        {fetching ? 'Syncing...' : 'Sync Data'}
                    </button>
                    <button
                        className="btn btn-primary"
                        onClick={() => setShowConnectModal(true)}
                    >
                        <Plus size={18} />
                        Connect Platform
                    </button>
                </div>
            </div>

            {/* Platform Connection Status */}
            {platformConnections.length > 0 && (
                <div className="card" style={{ marginBottom: 'var(--space-6)' }}>
                    <h3 style={{ marginBottom: 'var(--space-4)' }}>Connected Platforms</h3>
                    <div style={{ display: 'flex', gap: 'var(--space-4)', flexWrap: 'wrap' }}>
                        {platformConnections.map(conn => (
                            <div
                                key={conn.id}
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 'var(--space-2)',
                                    padding: 'var(--space-3) var(--space-4)',
                                    background: 'var(--color-bg-secondary)',
                                    borderRadius: 'var(--radius-lg)',
                                    border: `1px solid ${conn.status === 'active' ? 'var(--color-success)' : 'var(--color-error)'}`
                                }}
                            >
                                <div style={{
                                    width: '8px',
                                    height: '8px',
                                    borderRadius: '50%',
                                    background: conn.status === 'active' ? 'var(--color-success)' : 'var(--color-error)'
                                }} />
                                <span style={{ fontWeight: 500, textTransform: 'capitalize' }}>
                                    {conn.platform}
                                </span>
                                {conn.platformAccountName && (
                                    <span className="text-secondary" style={{ fontSize: 'var(--font-size-sm)' }}>
                                        ({conn.platformAccountName})
                                    </span>
                                )}
                                <button
                                    onClick={() => handleDisconnectClick(conn)}
                                    disabled={disconnecting === conn.id}
                                    title="Disconnect"
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        width: '20px',
                                        height: '20px',
                                        padding: 0,
                                        marginLeft: 'var(--space-2)',
                                        background: 'transparent',
                                        border: 'none',
                                        borderRadius: '50%',
                                        cursor: disconnecting === conn.id ? 'wait' : 'pointer',
                                        opacity: disconnecting === conn.id ? 0.5 : 0.6,
                                        color: 'var(--color-text-secondary)',
                                        transition: 'opacity 0.2s, color 0.2s'
                                    }}
                                    onMouseEnter={(e) => {
                                        if (disconnecting !== conn.id) {
                                            e.currentTarget.style.opacity = '1';
                                            e.currentTarget.style.color = 'var(--color-error)';
                                        }
                                    }}
                                    onMouseLeave={(e) => {
                                        e.currentTarget.style.opacity = '0.6';
                                        e.currentTarget.style.color = 'var(--color-text-secondary)';
                                    }}
                                >
                                    <X size={14} />
                                </button>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* Stats Cards */}
            <div className="grid grid-cols-4 gap-6" style={{ marginBottom: 'var(--space-8)' }}>
                <div className="stat-card">
                    <div style={{ fontSize: 'var(--font-size-2xl)', marginBottom: 'var(--space-2)' }}>
                        <BarChart3 size={24} />
                    </div>
                    <div className="stat-value">{activeCampaigns}</div>
                    <div className="stat-label">Active Campaigns</div>
                </div>

                <div className="stat-card">
                    <div style={{ fontSize: 'var(--font-size-2xl)', marginBottom: 'var(--space-2)' }}>
                        <DollarSign size={24} />
                    </div>
                    <div className="stat-value">{formatCurrency(stats.totalSpend)}</div>
                    <div className="stat-label">Total Spend</div>
                </div>

                <div className="stat-card">
                    <div style={{ fontSize: 'var(--font-size-2xl)', marginBottom: 'var(--space-2)' }}>
                        <Target size={24} />
                    </div>
                    <div className="stat-value">{formatCurrency(stats.totalRevenue)}</div>
                    <div className="stat-label">Total Revenue</div>
                </div>

                <div className="stat-card">
                    <div style={{ fontSize: 'var(--font-size-2xl)', marginBottom: 'var(--space-2)' }}>
                        <TrendingUp size={24} />
                    </div>
                    <div className="stat-value">{stats.avgRoas.toFixed(2)}x</div>
                    <div className="stat-label">Average ROAS</div>
                </div>
            </div>

            {/* Platform Filter & Campaigns Table */}
            <div className="card">
                <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: 'var(--space-6)',
                    flexWrap: 'wrap',
                    gap: 'var(--space-4)'
                }}>
                    <h2 style={{ margin: 0 }}>Campaigns ({filteredCampaigns.length})</h2>

                    <div style={{ display: 'flex', gap: 'var(--space-3)', alignItems: 'center' }}>
                        {/* Date Range Selector */}
                        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
                            <Calendar size={18} style={{ color: 'var(--color-text-secondary)' }} />
                            <select
                                className="select-field"
                                value={selectedDateRange}
                                onChange={(e) => handleDateRangeChange(e.target.value)}
                                disabled={fetching}
                                style={{ width: 'auto', minWidth: '160px' }}
                            >
                                <optgroup label="Quick Ranges">
                                    <option value={DATE_RANGE_PRESETS.TODAY}>{DATE_RANGE_LABELS[DATE_RANGE_PRESETS.TODAY]}</option>
                                    <option value={DATE_RANGE_PRESETS.YESTERDAY}>{DATE_RANGE_LABELS[DATE_RANGE_PRESETS.YESTERDAY]}</option>
                                    <option value={DATE_RANGE_PRESETS.LAST_7_DAYS}>{DATE_RANGE_LABELS[DATE_RANGE_PRESETS.LAST_7_DAYS]}</option>
                                    <option value={DATE_RANGE_PRESETS.LAST_14_DAYS}>{DATE_RANGE_LABELS[DATE_RANGE_PRESETS.LAST_14_DAYS]}</option>
                                    <option value={DATE_RANGE_PRESETS.LAST_30_DAYS}>{DATE_RANGE_LABELS[DATE_RANGE_PRESETS.LAST_30_DAYS]}</option>
                                </optgroup>
                                <optgroup label="Week">
                                    <option value={DATE_RANGE_PRESETS.THIS_WEEK}>{DATE_RANGE_LABELS[DATE_RANGE_PRESETS.THIS_WEEK]}</option>
                                    <option value={DATE_RANGE_PRESETS.LAST_WEEK}>{DATE_RANGE_LABELS[DATE_RANGE_PRESETS.LAST_WEEK]}</option>
                                </optgroup>
                                <optgroup label="Month">
                                    <option value={DATE_RANGE_PRESETS.THIS_MONTH}>{DATE_RANGE_LABELS[DATE_RANGE_PRESETS.THIS_MONTH]}</option>
                                    <option value={DATE_RANGE_PRESETS.LAST_MONTH}>{DATE_RANGE_LABELS[DATE_RANGE_PRESETS.LAST_MONTH]}</option>
                                </optgroup>
                                <optgroup label="Quarter">
                                    <option value={DATE_RANGE_PRESETS.THIS_QUARTER}>{DATE_RANGE_LABELS[DATE_RANGE_PRESETS.THIS_QUARTER]}</option>
                                    <option value={DATE_RANGE_PRESETS.LAST_QUARTER}>{DATE_RANGE_LABELS[DATE_RANGE_PRESETS.LAST_QUARTER]}</option>
                                </optgroup>
                                <optgroup label="Year">
                                    <option value={DATE_RANGE_PRESETS.THIS_YEAR}>{DATE_RANGE_LABELS[DATE_RANGE_PRESETS.THIS_YEAR]}</option>
                                    <option value={DATE_RANGE_PRESETS.LAST_YEAR}>{DATE_RANGE_LABELS[DATE_RANGE_PRESETS.LAST_YEAR]}</option>
                                </optgroup>
                                <optgroup label="Other">
                                    <option value={DATE_RANGE_PRESETS.ALL_TIME}>{DATE_RANGE_LABELS[DATE_RANGE_PRESETS.ALL_TIME]}</option>
                                </optgroup>
                            </select>
                        </div>

                        {/* Platform Filter */}
                        <select
                            className="select-field"
                            value={selectedPlatform}
                            onChange={(e) => setSelectedPlatform(e.target.value)}
                            style={{ width: 'auto', minWidth: '140px' }}
                        >
                            <option value="all">All Platforms</option>
                            <option value="meta">Meta Ads</option>
                            <option value="google">Google Ads</option>
                            <option value="tiktok">TikTok Ads</option>
                            <option value="amazon">Amazon Ads</option>
                        </select>
                    </div>
                </div>

                {/* Date Range Indicator */}
                <div style={{
                    marginBottom: 'var(--space-4)',
                    fontSize: 'var(--font-size-sm)',
                    color: 'var(--color-text-secondary)'
                }}>
                    Showing data for: {getDateRangeDescription(dateRange.startDate, dateRange.endDate)}
                </div>

                {loading ? (
                    <div style={{ textAlign: 'center', padding: 'var(--space-8)' }}>
                        <RefreshCw size={32} className="spinning" />
                        <p className="text-secondary">Loading campaigns...</p>
                    </div>
                ) : filteredCampaigns.length === 0 ? (
                    <div style={{ textAlign: 'center', padding: 'var(--space-8)', color: 'var(--color-text-secondary)' }}>
                        {platformConnections.length === 0 ? (
                            <>
                                <BarChart3 size={48} style={{ margin: '0 auto var(--space-4)' }} />
                                <h3>No Platforms Connected</h3>
                                <p>Connect your advertising platforms to start monitoring campaigns.</p>
                                <button
                                    className="btn btn-primary"
                                    style={{ marginTop: 'var(--space-4)' }}
                                    onClick={() => setShowConnectModal(true)}
                                >
                                    Connect Platform
                                </button>
                            </>
                        ) : (
                            <>
                                <BarChart3 size={48} style={{ margin: '0 auto var(--space-4)' }} />
                                <h3>No Campaigns Found</h3>
                                <p>Click "Sync Data" to fetch campaigns from your connected platforms.</p>
                                <button
                                    className="btn btn-primary"
                                    style={{ marginTop: 'var(--space-4)' }}
                                    onClick={handleRefresh}
                                    disabled={fetching}
                                >
                                    {fetching ? 'Syncing...' : 'Sync Data'}
                                </button>
                            </>
                        )}
                    </div>
                ) : (
                    <div style={{ overflowX: 'auto' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                            <thead>
                                <tr style={{ borderBottom: '2px solid var(--color-border)' }}>
                                    <th style={{ padding: 'var(--space-3)', textAlign: 'left' }} title="Campaign name and objective">Campaign</th>
                                    <th style={{ padding: 'var(--space-3)', textAlign: 'left' }} title="Advertising platform (Meta, Google, TikTok, Amazon)">Platform</th>
                                    <th style={{ padding: 'var(--space-3)', textAlign: 'left' }} title="Current campaign status: Active, Paused, or Ended">Status</th>
                                    <th style={{ padding: 'var(--space-3)', textAlign: 'right' }} title="Total number of times your ads were shown to users">Impressions</th>
                                    <th style={{ padding: 'var(--space-3)', textAlign: 'right' }} title="Total number of clicks on your ads">Clicks</th>
                                    <th style={{ padding: 'var(--space-3)', textAlign: 'right' }} title="Click-Through Rate: Percentage of impressions that resulted in a click (Clicks ÷ Impressions × 100)">CTR</th>
                                    <th style={{ padding: 'var(--space-3)', textAlign: 'right' }} title="Cost Per Click: Average cost paid for each click (Spend ÷ Clicks)">CPC</th>
                                    <th style={{ padding: 'var(--space-3)', textAlign: 'right' }} title="Cost Per Mille: Cost per 1,000 impressions (Spend ÷ Impressions × 1000)">CPM</th>
                                    <th style={{ padding: 'var(--space-3)', textAlign: 'right' }} title="Total amount spent on this campaign">Spend</th>
                                    <th style={{ padding: 'var(--space-3)', textAlign: 'right' }} title="Total number of conversions (purchases, sign-ups, or other goal completions)">Conv.</th>
                                    <th style={{ padding: 'var(--space-3)', textAlign: 'right' }} title="Cost Per Conversion: Average cost to acquire one conversion (Spend ÷ Conversions)">Cost/Conv.</th>
                                    <th style={{ padding: 'var(--space-3)', textAlign: 'right' }} title="Total revenue generated from conversions attributed to this campaign">Revenue</th>
                                    <th style={{ padding: 'var(--space-3)', textAlign: 'right' }} title="Return On Ad Spend: Revenue generated per dollar spent (Revenue ÷ Spend). Values above 1x indicate profit.">ROAS</th>
                                    <th style={{ padding: 'var(--space-3)', width: '40px' }}></th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredCampaigns.map(campaign => {
                                    const isExpanded = expandedCampaignId === campaign.id;
                                    return (
                                    <React.Fragment key={campaign.id}>
                                    <tr
                                        style={{
                                            borderBottom: isExpanded ? 'none' : '1px solid var(--color-border)',
                                            cursor: 'pointer',
                                            background: isExpanded ? 'var(--color-bg-hover)' : 'transparent'
                                        }}
                                        onClick={() => {
                                            setExpandedCampaignId(isExpanded ? null : campaign.id);
                                        }}
                                        onMouseEnter={(e) => {
                                            if (!isExpanded) e.currentTarget.style.background = 'var(--color-bg-hover)';
                                        }}
                                        onMouseLeave={(e) => {
                                            if (!isExpanded) e.currentTarget.style.background = 'transparent';
                                        }}
                                    >
                                        <td style={{ padding: 'var(--space-3)' }}>
                                            <div style={{ fontWeight: 500 }}>{campaign.name}</div>
                                            <div className="text-secondary" style={{ fontSize: 'var(--font-size-sm)' }}>
                                                {campaign.objective}
                                            </div>
                                        </td>
                                        <td style={{ padding: 'var(--space-3)', textTransform: 'capitalize' }}>
                                            {campaign.platform}
                                        </td>
                                        <td style={{ padding: 'var(--space-3)' }}>
                                            <span
                                                style={{
                                                    padding: 'var(--space-1) var(--space-3)',
                                                    borderRadius: 'var(--radius-full)',
                                                    fontSize: 'var(--font-size-sm)',
                                                    background: campaign.status === 'ACTIVE' ? 'var(--color-success-bg)' :
                                                               campaign.status === 'PAUSED' ? 'var(--color-warning-bg)' :
                                                               'var(--color-error-bg)',
                                                    color: campaign.status === 'ACTIVE' ? 'var(--color-success)' :
                                                          campaign.status === 'PAUSED' ? 'var(--color-warning)' :
                                                          'var(--color-error)'
                                                }}
                                            >
                                                {campaign.status}
                                            </span>
                                        </td>
                                        <td style={{ padding: 'var(--space-3)', textAlign: 'right' }}>
                                            {formatNumber(campaign.currentMetrics?.impressions || 0)}
                                        </td>
                                        <td style={{ padding: 'var(--space-3)', textAlign: 'right' }}>
                                            {formatNumber(campaign.currentMetrics?.clicks || 0)}
                                        </td>
                                        <td style={{ padding: 'var(--space-3)', textAlign: 'right' }}>
                                            {formatPercent(campaign.currentMetrics?.ctr || 0)}
                                        </td>
                                        <td style={{ padding: 'var(--space-3)', textAlign: 'right' }}>
                                            {formatCurrency(campaign.currentMetrics?.cpc || 0)}
                                        </td>
                                        <td style={{ padding: 'var(--space-3)', textAlign: 'right' }}>
                                            {formatCurrency(campaign.currentMetrics?.cpm || ((campaign.currentMetrics?.spend || 0) / (campaign.currentMetrics?.impressions || 1)) * 1000)}
                                        </td>
                                        <td style={{ padding: 'var(--space-3)', textAlign: 'right' }}>
                                            {formatCurrency(campaign.currentMetrics?.spend || 0)}
                                        </td>
                                        <td style={{ padding: 'var(--space-3)', textAlign: 'right' }}>
                                            {formatNumber(campaign.currentMetrics?.conversions || 0)}
                                        </td>
                                        <td style={{ padding: 'var(--space-3)', textAlign: 'right' }}>
                                            {formatCurrency(campaign.currentMetrics?.cpa || 0)}
                                        </td>
                                        <td style={{ padding: 'var(--space-3)', textAlign: 'right' }}>
                                            {formatCurrency(campaign.currentMetrics?.revenue || 0)}
                                        </td>
                                        <td style={{
                                            padding: 'var(--space-3)',
                                            textAlign: 'right',
                                            fontWeight: 500,
                                            color: (campaign.currentMetrics?.roas || 0) >= 3 ? 'var(--color-success)' :
                                                  (campaign.currentMetrics?.roas || 0) >= 1 ? 'var(--color-warning)' :
                                                  'var(--color-error)'
                                        }}>
                                            {(campaign.currentMetrics?.roas || 0).toFixed(2)}x
                                        </td>
                                        <td style={{ padding: 'var(--space-3)', textAlign: 'center' }}>
                                            {isExpanded ? (
                                                <ChevronDown size={18} style={{ color: 'var(--color-primary)' }} />
                                            ) : (
                                                <ChevronRight size={18} style={{ color: 'var(--color-text-secondary)' }} />
                                            )}
                                        </td>
                                    </tr>
                                    {isExpanded && (
                                        <CampaignDetailRow
                                            campaign={campaign}
                                            dateRange={dateRange}
                                            userId={currentUser?.uid}
                                            brandId={selectedBrand?.id}
                                            onCollapse={() => setExpandedCampaignId(null)}
                                        />
                                    )}
                                    </React.Fragment>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {/* Platform Connect Modal */}
            <PlatformConnectModal
                isOpen={showConnectModal}
                onClose={(success) => {
                    setShowConnectModal(false);
                    if (success) {
                        // Refresh connections list
                        fetchConnections();
                    }
                }}
                brand={selectedBrand}
            />

            {/* Disconnect Confirmation Modal */}
            <ConfirmationModal
                isOpen={showDisconnectModal}
                onClose={() => {
                    setShowDisconnectModal(false);
                    setConnectionToDisconnect(null);
                }}
                onConfirm={handleDisconnect}
                title="Disconnect Platform"
                message={`Are you sure you want to disconnect ${connectionToDisconnect?.platform}${connectionToDisconnect?.platformAccountName ? ` (${connectionToDisconnect.platformAccountName})` : ''}? This will stop syncing campaigns from this account.`}
                confirmText="Disconnect"
                cancelText="Cancel"
                variant="danger"
            />
        </div>
    );
};

export default CampaignMonitoring;
